import React, { useMemo } from 'react'
import Box from '@cloudscape-design/components/box'
import { getToolUrl } from '@services/ToolMenuConfigService'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import './OrdersView.scss'

/**
 * OrdersView — embeds the Orders page (JWOStoreOrdersWebsite) as an iframe filling the tab panel.
 *
 * Resolved through the centralized tool registry. ORDERS_ASSET_ID must equal ASSET_ID in
 * JwoOrdersTool.constants.ts (JWOStoreManagementPlatformRegistration) and the TOOL_ID the website
 * passes to PlatformClient.init; ORDERS_DISPLAY_NAME must equal its registered display name, because
 * getToolUrl resolves the URL from it.
 */
export const ORDERS_DISPLAY_NAME = 'Orders'
export const ORDERS_ASSET_ID = 'jwo-store-orders-website'

/** The registered URL with any hash removed; the Orders page has no hash route. '' if unresolved. */
export const buildOrdersUrl = (): string => {
    const base = getToolUrl(ORDERS_DISPLAY_NAME, ORDERS_ASSET_ID)
    if (!base) {
        console.warn('OperationsPage: could not resolve Orders URL')
        return ''
    }
    const hashIndex = base.indexOf('#')
    return hashIndex !== -1 ? base.substring(0, hashIndex) : base
}

const OrdersView: React.FC = () => {
    const [bundle] = useBundle(BUNDLE_NAME)
    // Computed once, deliberately not keyed on the selected store (unlike StoreHealthView): Orders
    // spans every store the user may view, and a reload would discard the user's filters.
    const ordersUrl = useMemo(() => buildOrdersUrl(), [])

    if (!ordersUrl) {
        return (
            <Box padding="l" color="text-status-inactive">
                {t(bundle, 'jsma-web-operations-ordersUnavailable')}
            </Box>
        )
    }

    return (
        <div className="operations-orders-container" data-atb-id="operations-orders-container">
            <iframe
                className="operations-orders-frame"
                data-atb-id="operations-orders-frame"
                title={t(bundle, 'jsma-web-operations-ordersTitle')}
                aria-label={t(bundle, 'jsma-web-operations-ordersTitle')}
                src={ordersUrl}
            />
        </div>
    )
}

export default OrdersView
